import clsx from "clsx";
import {
  useEffect,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";

import type { EditorNoteBlock } from "@story/data-centers-hub/config/editorNoteBlocks";
import {
  hubStateOutlines,
  type HubStateOutlineId,
} from "@story/data-centers-hub/config/stateOutlines";
import type { DeckSlideId } from "@story/data-centers-hub/config/slides";
import {
  firstScalarChar,
  removeFirstWordFromHtml,
  wordTokensFromHtml,
} from "src/utils/paragraphMarkup";

import * as styles from "./IntroCovers.css";

const CHAPTER_CARD_LABEL: Record<HubStateOutlineId, string> = {
  virginia: "Virginia",
  utah: "Utah",
  arizona: "Arizona",
};

type ChapterHeading = Extract<EditorNoteBlock, { type: "heading" }> & {
  slideId: DeckSlideId;
  state: HubStateOutlineId;
};

type NoteRenderItem =
  | Exclude<EditorNoteBlock, { type: "separator" }>
  | {
      type: "chapter";
      heading: ChapterHeading;
      paragraphs: ReadonlyArray<
        Extract<EditorNoteBlock, { type: "paragraph" }>
      >;
    };

function NoteParagraph({
  html,
  dropcap = false,
}: {
  html: string;
  dropcap?: boolean;
}) {
  const tokens = wordTokensFromHtml(html);
  const useDropcap = dropcap && tokens.length > 0;
  const firstWord = useDropcap ? (tokens[0] ?? "") : "";
  let bodyHtml = html.replace(/&nbsp;/gi, " ");
  if (useDropcap) {
    bodyHtml = removeFirstWordFromHtml(bodyHtml);
  }
  const firstLetter = firstScalarChar(firstWord);
  const rest = firstWord.slice(firstLetter.length);

  return (
    <p>
      {useDropcap ? (
        <>
          <span aria-hidden="true">
            <span className={styles.noteDropcap}>{firstLetter}</span>
            {rest}
          </span>
          <span className={styles.srOnly}>{firstWord}</span>
        </>
      ) : null}
      <span dangerouslySetInnerHTML={{ __html: bodyHtml }} />
    </p>
  );
}

function StateOutline({
  state,
  animate,
  scrollRoot,
}: {
  state: HubStateOutlineId;
  animate: boolean;
  scrollRoot: RefObject<HTMLElement | null> | null;
}) {
  const pathRef = useRef<SVGPathElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  // Survive hold → wiping → done → return without replaying the draw.
  const drawnRef = useRef(!animate);

  useEffect(() => {
    const path = pathRef.current;
    const host = svgRef.current;
    if (!path || !host) {
      return;
    }
    const length = path.getTotalLength();
    path.style.strokeDasharray = `${length}`;

    const finish = (withEase: boolean) => {
      if (withEase) {
        path.style.transition =
          "stroke-dashoffset 1.15s cubic-bezier(0.4, 0, 0.2, 1)";
      } else {
        path.style.transition = "none";
      }
      path.style.strokeDashoffset = "0";
      drawnRef.current = true;
    };

    if (!animate || drawnRef.current) {
      finish(false);
      return;
    }

    path.style.transition = "none";
    path.style.strokeDashoffset = `${length}`;

    const root = scrollRoot?.current ?? null;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }
        // Double rAF so the initial dashoffset paint lands before the ease.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => finish(true));
        });
        observer.disconnect();
      },
      // Eager — chapter cards sit low in the note; a strict threshold left
      // Arizona stuck fully dashed (invisible / half-drawn).
      { root, rootMargin: "0px", threshold: 0.01 },
    );
    observer.observe(host);
    return () => observer.disconnect();
  }, [animate, scrollRoot, state]);

  const outline = hubStateOutlines[state];
  const viewBoxParts = outline.viewBox.split(/\s+/).map(Number);
  const vbW = viewBoxParts[2] ?? 1;
  const vbH = viewBoxParts[3] ?? 1;
  return (
    <svg
      ref={svgRef}
      className={styles.stateOutline}
      viewBox={outline.viewBox}
      // Pin ratio from viewBox — don’t trust width:auto intrinsic sizing alone.
      style={{ aspectRatio: `${vbW} / ${vbH}` }}
      aria-hidden="true"
      focusable="false"
    >
      <path
        ref={pathRef}
        className={styles.stateOutlinePath}
        d={outline.d}
      />
    </svg>
  );
}

/** Nested anchors inside a group link are invalid — keep the label markup. */
function stripAnchors(html: string): string {
  return html
    .replace(/<\/?a\b[^>]*>/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

function chapterHref(slideId: DeckSlideId): string {
  if (slideId === "virginia") {
    return "#loudoun";
  }
  if (slideId === "utah") {
    return "#utah";
  }
  if (slideId === "arizona") {
    return "#arizona";
  }
  return `#${slideId}`;
}

function isChapterHeading(
  block: EditorNoteBlock,
): block is ChapterHeading {
  return (
    block.type === "heading" &&
    block.slideId != null &&
    block.state != null
  );
}

/**
 * Fold each chapter hed (+ optional following graf) into one card. Hed stays;
 * dek paragraphs are not painted — CMS can clear them; the first following
 * graf is still consumed so leftover dek does not spill into the note.
 * Separators are dropped — card borders replace them.
 */
function groupNoteBlocks(
  blocks: ReadonlyArray<EditorNoteBlock>,
): ReadonlyArray<NoteRenderItem> {
  const out: NoteRenderItem[] = [];
  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i]!;
    if (block.type === "separator") {
      i += 1;
      continue;
    }
    if (isChapterHeading(block)) {
      const paragraphs: Array<
        Extract<EditorNoteBlock, { type: "paragraph" }>
      > = [];
      i += 1;
      if (blocks[i]?.type === "paragraph") {
        paragraphs.push(
          blocks[i]! as Extract<EditorNoteBlock, { type: "paragraph" }>,
        );
        i += 1;
      }
      out.push({ type: "chapter", heading: block, paragraphs });
      continue;
    }
    out.push(block);
    i += 1;
  }
  return out;
}

function ChapterCard({
  heading,
  animateOutlines,
  scrollRoot,
  armed,
}: {
  heading: ChapterHeading;
  animateOutlines: boolean;
  scrollRoot: RefObject<HTMLElement | null> | null;
  /** Hold accent fill through a deeplink wipe (hover drops on pointer-up). */
  armed?: boolean;
}) {
  const Tag = (`h${heading.level}` as "h1" | "h2" | "h3" | "h4" | "h5" | "h6");
  const hedHtml = stripAnchors(heading.html);

  return (
    <a
      className={styles.chapterCard}
      href={chapterHref(heading.slideId)}
      data-state={heading.state}
      // Hed is visible; keep a name when CMS leaves the heading empty.
      {...(hedHtml
        ? {}
        : { "aria-label": CHAPTER_CARD_LABEL[heading.state] })}
      {...(armed ? { "data-armed": "" } : {})}
    >
      <Tag
        className={styles.chapterCardHed}
        dangerouslySetInnerHTML={{ __html: hedHtml }}
      />
      <StateOutline
        state={heading.state}
        animate={animateOutlines}
        scrollRoot={scrollRoot}
      />
    </a>
  );
}

export function NoteBlocks({
  blocks,
  animateOutlines = false,
  scrollRoot = null,
  armedState = null,
}: {
  blocks: ReadonlyArray<EditorNoteBlock>;
  /** Live note scroller only — square wipe copies stay fully drawn. */
  animateOutlines?: boolean;
  scrollRoot?: RefObject<HTMLElement | null> | null;
  /** Chapter outline whose card stays filled through a deeplink wipe. */
  armedState?: HubStateOutlineId | null;
}): ReactNode {
  const items = groupNoteBlocks(blocks);
  return (
    <>
      {items.map((item, index) => {
        switch (item.type) {
          case "chapter":
            return (
              <ChapterCard
                key={`chapter-${item.heading.slideId}-${index}`}
                heading={item.heading}
                animateOutlines={animateOutlines}
                scrollRoot={scrollRoot}
                armed={armedState === item.heading.state}
              />
            );
          case "paragraph":
            return (
              <NoteParagraph
                key={index}
                html={item.html}
                dropcap={item.dropcap}
              />
            );
          case "heading":
            // Non-chapter headings (no slide / state) stay plain.
            {
              const Tag = (`h${item.level}` as
                | "h1"
                | "h2"
                | "h3"
                | "h4"
                | "h5"
                | "h6");
              return (
                <Tag
                  key={index}
                  className={styles.noteHeading}
                  dangerouslySetInnerHTML={{ __html: item.html }}
                />
              );
            }
          case "pullquote":
            return (
              <blockquote key={index} className={styles.notePullquote}>
                <div dangerouslySetInnerHTML={{ __html: item.html }} />
                {item.citationHtml ? (
                  <cite
                    className={styles.notePullquoteCite}
                    dangerouslySetInnerHTML={{ __html: item.citationHtml }}
                  />
                ) : null}
              </blockquote>
            );
          default:
            return null;
        }
      })}
    </>
  );
}

export function NoteBlocksColumn({
  blocks,
  cue,
  className,
  columnRef,
  animateOutlines,
  scrollRoot,
  armedState = null,
}: {
  blocks: ReadonlyArray<EditorNoteBlock>;
  cue: ReactNode;
  className?: string;
  columnRef?: RefObject<HTMLDivElement | null>;
  animateOutlines?: boolean;
  scrollRoot?: RefObject<HTMLElement | null> | null;
  armedState?: HubStateOutlineId | null;
}) {
  return (
    <div ref={columnRef} className={clsx(styles.noteColumn, className)}>
      <NoteBlocks
        blocks={blocks}
        animateOutlines={animateOutlines}
        scrollRoot={scrollRoot}
        armedState={armedState}
      />
      {cue}
    </div>
  );
}
